import { Dialog } from '@angular/cdk/dialog';
import { inject, Injectable } from '@angular/core';
import {
  IInitEventDisruptionProtocol,
  InitEventDisruptionApiService,
  REACTIVE_API_TOPICS,
  ReactingProtocolApiService,
  ReactiveApiBrokerService,
} from 'ngx-mt-ui-lib/api-client';
import { openLazyDialog } from '@app/shared/functions/open-lazy-dialog.function';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
  DialogComponent,
  IDialogComponentData,
  openConfirmDialog,
} from 'ngx-mt-ui-lib/components/dialog';
import {
  catchError,
  distinctUntilChanged,
  EMPTY,
  filter,
  from,
  map,
  of,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { withLatestFrom } from 'rxjs/operators';

import { DisruptionActions } from './disruption.actions';
import { mapDisruptionProtocols } from './disruption.mappers';
import {
  selectActiveProtocol,
  selectDisruptionHasChanges,
  selectDisruptionId,
} from './disruption.selectors';

@Injectable()
export class DisruptionProtocolsEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly reactiveApiBroker = inject(ReactiveApiBrokerService);
  private readonly translateService = inject(TranslateService);
  private readonly disruptionApiService = inject(InitEventDisruptionApiService);
  private readonly reactingProtocolApiService = inject(
    ReactingProtocolApiService,
  );
  private readonly dialog = inject(Dialog);

  readonly watchProtocols$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.watchProtocols),
      switchMap(() =>
        this.store.select(selectDisruptionId).pipe(
          distinctUntilChanged(),
          switchMap((disruptionId) => {
            if (typeof disruptionId !== 'number' || !disruptionId) {
              return EMPTY;
            }

            return this.reactiveApiBroker
              .watchParsed<
                IInitEventDisruptionProtocol[]
              >(REACTIVE_API_TOPICS.disruptionProtocols(disruptionId))
              .pipe(
                filter(
                  (protocols): protocols is IInitEventDisruptionProtocol[] =>
                    Array.isArray(protocols),
                ),
                map((protocols) =>
                  DisruptionActions.requestProtocolsSuccess({
                    snapshot: mapDisruptionProtocols(
                      protocols,
                      this.translateService,
                    ),
                    silent: true,
                  }),
                ),
              );
          }),
          takeUntil(
            this.actions$.pipe(ofType(DisruptionActions.stopWatchProtocols)),
          ),
        ),
      ),
    ),
  );

  readonly requestProtocols$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.requestProtocols),
      withLatestFrom(this.store.select(selectDisruptionId)),
      switchMap(([{ silent }, disruptionId]) => {
        if (typeof disruptionId !== 'number') {
          return EMPTY;
        }

        return this.disruptionApiService.getProtocols(disruptionId).pipe(
          map((protocols) =>
            DisruptionActions.requestProtocolsSuccess({
              snapshot: mapDisruptionProtocols(
                protocols,
                this.translateService,
              ),
              silent,
            }),
          ),
          catchError((error) =>
            of(DisruptionActions.requestProtocolsError({ error, silent })),
          ),
        );
      }),
    ),
  );

  readonly attemptRequestRunProtocol$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.attemptRequestRunProtocol),
      withLatestFrom(this.store.select(selectDisruptionHasChanges)),
      switchMap(([params, hasChanges]) => {
        if (!hasChanges) {
          return of(DisruptionActions.requestRunProtocol(params));
        }

        return openConfirmDialog(this.dialog).closed.pipe(
          switchMap((res) => {
            if (!res) {
              return EMPTY;
            }

            return of(DisruptionActions.requestRunProtocol(params));
          }),
        );
      }),
    ),
  );

  readonly requestRunProtocol$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.requestRunProtocol),
      withLatestFrom(this.store.select(selectDisruptionId)),
      switchMap(([{ templateVersionId, methodSelection }, disruptionId]) => {
        return this.reactingProtocolApiService
          .runProtocol({
            templateVersionId,
            disruptionId,
            methodSelection,
          })
          .pipe(
            map(() => DisruptionActions.requestRunProtocolSuccess()),
            catchError((error) =>
              of(DisruptionActions.requestRunProtocolError({ error })),
            ),
          );
      }),
    ),
  );

  readonly requestRunProtocolSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DisruptionActions.requestRunProtocolSuccess),
        withLatestFrom(this.store.select(selectDisruptionId)),
        tap(([_, id]) => {
          if (typeof id == 'number') {
            this.store.dispatch(DisruptionActions.requestProtocols({}));
          }
        }),
      ),
    { dispatch: false },
  );

  readonly attemptCancelProtocol$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.attemptCancelProtocol),
      withLatestFrom(this.store.select(selectActiveProtocol)),
      switchMap(([_, { id }]) => {
        if (!id) {
          return EMPTY;
        }

        const ref = this.dialog.open<
          boolean,
          IDialogComponentData,
          DialogComponent
        >(DialogComponent, {
          autoFocus: false,
          data: {
            content:
              'EVENT.DISRUPTION.CARD.PROTOCOL_RUNNER.FUNCTIONS.CANCEL.QUESTION',
            width: '28.75rem',
          },
        });

        return from(ref.closed).pipe(
          switchMap((res) => {
            if (!res) {
              return EMPTY;
            }

            return of(
              DisruptionActions.requestCancelProtocol({ protocolId: id }),
            );
          }),
        );
      }),
    ),
  );

  readonly requestCancelProtocol$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.requestCancelProtocol),
      switchMap(({ protocolId }) =>
        this.reactingProtocolApiService.cancelProtocol(protocolId).pipe(
          map(() => DisruptionActions.requestCancelProtocolSuccess()),
          catchError((error) =>
            of(DisruptionActions.requestCancelProtocolError({ error })),
          ),
        ),
      ),
    ),
  );

  readonly requestCancelProtocolSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DisruptionActions.requestCancelProtocolSuccess),
        tap(() => {
          this.store.dispatch(DisruptionActions.requestProtocols({}));
        }),
      ),
    { dispatch: false },
  );

  readonly openAddProtocolStepModal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DisruptionActions.openAddProtocolStepModal),
        switchMap(({ protocolId }) =>
          openLazyDialog(
            this.dialog,
            () =>
              import('../disruption-card/disruption-card-info/disruption-card-main-info/add-disruption-card-protocol-step-modal/add-disruption-card-protocol-step-modal.component'),
            'AddDisruptionCardProtocolStepModalComponent',
            {
              autoFocus: false,
              data: {
                protocolId,
              },
              panelClass: 'aligned-right-dialog',
            },
          ),
        ),
      ),
    { dispatch: false },
  );

  readonly requestAddProtocolStep$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.requestAddProtocolStep),
      switchMap(({ protocolId, body }) =>
        this.reactingProtocolApiService.addProtocolStep(protocolId, body).pipe(
          map(() => DisruptionActions.requestAddProtocolStepSuccess()),
          catchError((error) =>
            of(
              DisruptionActions.requestAddProtocolStepError({
                error,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly requestAddProtocolStepSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DisruptionActions.requestAddProtocolStepSuccess),
        tap(() => {
          this.store.dispatch(DisruptionActions.requestProtocols({}));
        }),
      ),
    { dispatch: false },
  );

  readonly attemptProtocolStepsDelete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.attemptProtocolStepsDelete),
      switchMap(({ stepId }) => {
        if (!stepId) {
          return EMPTY;
        }

        const ref = this.dialog.open<
          boolean,
          IDialogComponentData,
          DialogComponent
        >(DialogComponent, {
          autoFocus: false,
          data: {
            content:
              'EVENT.DISRUPTION.CARD.PROTOCOL_RUNNER.FUNCTIONS.DELETE_STEP.QUESTION',
            width: '28.75rem',
          },
        });

        return from(ref.closed).pipe(
          switchMap((res) => {
            if (!res) {
              return EMPTY;
            }

            return of(
              DisruptionActions.requestProtocolStepsDelete({
                stepId,
              }),
            );
          }),
        );
      }),
    ),
  );

  readonly requestProtocolStepsDelete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.requestProtocolStepsDelete),
      switchMap(({ stepId }) =>
        this.reactingProtocolApiService.deleteProtocolStep(stepId).pipe(
          map(() => DisruptionActions.requestProtocolStepsDeleteSuccess()),
          catchError((error) =>
            of(DisruptionActions.requestProtocolStepsDeleteError({ error })),
          ),
        ),
      ),
    ),
  );

  readonly requestProtocolStepsDeleteSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DisruptionActions.requestProtocolStepsDeleteSuccess),
        tap(() => {
          this.store.dispatch(DisruptionActions.requestProtocols({}));
        }),
      ),
    { dispatch: false },
  );

  readonly attemptProtocolStepsComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.attemptProtocolStepsComplete),
      switchMap(({ stepId, completedByOther }) => {
        if (!stepId) {
          return EMPTY;
        }

        const ref = this.dialog.open<
          boolean,
          IDialogComponentData,
          DialogComponent
        >(DialogComponent, {
          autoFocus: false,
          data: {
            content:
              'EVENT.DISRUPTION.CARD.PROTOCOL_RUNNER.FUNCTIONS.COMPLETE_STEP.QUESTION',
            width: '28.75rem',
          },
        });

        return from(ref.closed).pipe(
          switchMap((res) => {
            if (!res) {
              return EMPTY;
            }

            return of(
              DisruptionActions.requestProtocolStepsComplete({
                stepId,
                completedByOther,
              }),
            );
          }),
        );
      }),
    ),
  );

  readonly requestProtocolStepsComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.requestProtocolStepsComplete),
      switchMap(({ stepId, completedByOther }) =>
        this.reactingProtocolApiService
          .completeProtocolStep(stepId, completedByOther)
          .pipe(
            map(() => DisruptionActions.requestProtocolStepsCompleteSuccess()),
            catchError((error) =>
              of(
                DisruptionActions.requestProtocolStepsCompleteError({ error }),
              ),
            ),
          ),
      ),
    ),
  );

  readonly requestProtocolStepsCompleteSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DisruptionActions.requestProtocolStepsCompleteSuccess),
        tap(() => {
          this.store.dispatch(DisruptionActions.requestProtocols({}));
        }),
      ),
    { dispatch: false },
  );

  readonly attemptProtocolStepsCancel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.attemptProtocolStepsCancel),
      switchMap(({ stepId }) => {
        if (!stepId) {
          return EMPTY;
        }

        const ref = this.dialog.open<
          boolean,
          IDialogComponentData,
          DialogComponent
        >(DialogComponent, {
          autoFocus: false,
          data: {
            content:
              'EVENT.DISRUPTION.CARD.PROTOCOL_RUNNER.FUNCTIONS.CANCEL_STEP.QUESTION',
            width: '28.75rem',
          },
        });

        return from(ref.closed).pipe(
          switchMap((res) => {
            if (!res) {
              return EMPTY;
            }

            return of(
              DisruptionActions.requestProtocolStepsCancel({
                stepId,
              }),
            );
          }),
        );
      }),
    ),
  );

  readonly requestProtocolStepsCancel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.requestProtocolStepsCancel),
      switchMap(({ stepId }) =>
        this.reactingProtocolApiService.cancelProtocolStep(stepId).pipe(
          map(() => DisruptionActions.requestProtocolStepsCancelSuccess()),
          catchError((error) =>
            of(DisruptionActions.requestProtocolStepsCancelError({ error })),
          ),
        ),
      ),
    ),
  );

  readonly requestProtocolStepsCancelSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DisruptionActions.requestProtocolStepsCancelSuccess),
        tap(() => {
          this.store.dispatch(DisruptionActions.requestProtocols({}));
        }),
      ),
    { dispatch: false },
  );

  readonly requestEditProtocolStepReactRole$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.requestEditProtocolStepReactRole),
      switchMap(({ stepId, reactRoleId }) =>
        this.reactingProtocolApiService
          .editProtocolStepReactRole(stepId, reactRoleId)
          .pipe(
            map(() =>
              DisruptionActions.requestEditProtocolStepReactRoleSuccess(),
            ),
            catchError((error) =>
              of(
                DisruptionActions.requestEditProtocolStepReactRoleError({
                  error,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  readonly requestEditProtocolStepManualFinishedDate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.requestEditProtocolStepManualFinishedDate),
      switchMap(
        ({ protocolId, isActiveProtocol, stepId, manualFinishedDate }) =>
          this.reactingProtocolApiService
            .editProtocolStepManualFinishedDate(stepId, manualFinishedDate)
            .pipe(
              map(() =>
                DisruptionActions.requestEditProtocolStepManualFinishedDateSuccess(
                  { protocolId, isActiveProtocol },
                ),
              ),
              catchError((error) =>
                of(
                  DisruptionActions.requestEditProtocolStepManualFinishedDateError(
                    { protocolId, isActiveProtocol, error },
                  ),
                ),
              ),
            ),
      ),
    ),
  );

  readonly requestEditProtocolStepUpdateReactRole$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          DisruptionActions.requestEditProtocolStepReactRoleSuccess,
          DisruptionActions.requestEditProtocolStepReactRoleError,
        ),
        tap(() => {
          this.store.dispatch(DisruptionActions.requestProtocols({}));
        }),
      ),
    { dispatch: false },
  );

  readonly requestEditProtocolStepExecuteResult$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.requestEditProtocolStepExecuteResult),
      switchMap(({ stepId, executeResult }) =>
        this.reactingProtocolApiService
          .updateStepExecuteResult(stepId, { executeResult })
          .pipe(
            map(() =>
              DisruptionActions.requestEditProtocolStepExecuteResultSuccess(),
            ),
            catchError((error) =>
              of(
                DisruptionActions.requestEditProtocolStepExecuteResultError({
                  error,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  readonly requestEditProtocolStepUpdateManualFinishedDate$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          DisruptionActions.requestEditProtocolStepManualFinishedDateSuccess,
          DisruptionActions.requestEditProtocolStepManualFinishedDateError,
          DisruptionActions.requestEditProtocolStepExecuteResultSuccess,
          DisruptionActions.requestEditProtocolStepExecuteResultError,
        ),
        tap(() => {
          this.store.dispatch(DisruptionActions.requestProtocols({}));
        }),
      ),
    { dispatch: false },
  );
}
